import {
  Viewer,
  CesiumTerrainProvider,
  IonImageryProvider,
  UrlTemplateImageryProvider,
  ImageryLayer,
  Color,
  Ellipsoid,
  EllipsoidTerrainProvider,
  TerrainProvider,
} from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import GeoidCorrectedTerrainProvider from "./GeoidCorrectedTerrainProvider";
import { loadEgm96Grid } from "../utils/Egm96Grid";
import { DeferredValue } from "lib/actions/util";
import { deferred } from "lib/actions/util";

export default class TerrainPlugin implements AttachablePluginInterface {
  private readonly _viewer = new DeferredValue<Viewer>();
  private _currentWorldImageryAssetId: string = null;
  private _currentWorldImageryLayer: ImageryLayer = null;
  private _backgroundColor: string = "#000000";
  private _terrainProviderReadinessSignal = deferred<void>();
  private _latestTerrainRequest = 0;
  private readonly _assetIdRegex = /^self:(.+)$/;

  whenTerrainProviderIsReady(): Promise<{ isCancelled: boolean }> {
    return Promise.resolve(
      this._terrainProviderReadinessSignal as Promise<{ isCancelled: boolean }>,
    );
  }

  setViewer(viewer: Viewer) {
    this._viewer.set(viewer);
  }

  detachViewer() {
    this._viewer.clear();
  }

  get isCameraUnderground(): boolean {
    return this._viewer.current?.scene.cameraUnderground ?? false;
  }

  async setTerrain(assetId: string, isGeoidCorrectionEnabled: boolean) {
    const request = ++this._latestTerrainRequest;
    if (!assetId) {
      this.setTerrainMapTiler(assetId, request, isGeoidCorrectionEnabled);
    } else {
      const match = this._assetIdRegex.exec(assetId);
      if (match) {
        this.setTerrainMapTiler(match[1], request, isGeoidCorrectionEnabled);
      } else {
        this.setTerrainIon(assetId, request, isGeoidCorrectionEnabled);
      }
    }
  }

  async setWorldImagery(assetId: string) {
    if (!assetId) {
      this.setWorldImageryMapTiler(assetId);
    } else {
      const match = this._assetIdRegex.exec(assetId);
      if (match) {
        this.setWorldImageryMapTiler(match[1]);
      } else {
        this.setWorldImageryIon(assetId);
      }
    }
  }

  private async setTerrainIon(
    ionAssetId: string,
    request: number,
    isGeoidCorrectionEnabled: boolean,
  ) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    const ionId = this.parseIonAssetId(
      ionAssetId || process.env.NEXT_PUBLIC_DEFAULT_TERRAIN_EXTERNAL_ID,
    );
    if (!ionId) return;

    let terrainProvider: TerrainProvider;
    try {
      terrainProvider = await CesiumTerrainProvider.fromIonAssetId(ionId);
    } catch {
      // Fall back to the MapTiler default
      this.setTerrainMapTiler(null, request, isGeoidCorrectionEnabled);
      return;
    }

    if (this.isStaleTerrainRequest(request)) return;

    viewer.terrainProvider = terrainProvider;
    this._terrainProviderReadinessSignal.ready();
  }

  private async setTerrainMapTiler(
    assetId: string,
    request: number,
    isGeoidCorrectionEnabled: boolean,
  ) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    const mapTilerKey = globalThis.__ENV?.RUNTIME_PUBLIC_MAPTILER_KEY;
    const defaultMapTilerTerrain = "terrain-quantized-mesh-v2";
    const mapTilerId = encodeURIComponent(assetId || defaultMapTilerTerrain);
    const shouldCorrectGeoid =
      isGeoidCorrectionEnabled && mapTilerId === defaultMapTilerTerrain;

    const providerLoad = CesiumTerrainProvider.fromUrl(
      `https://api.maptiler.com/tiles/${mapTilerId}/?key=${mapTilerKey}`,
      {
        requestVertexNormals: true,
      },
    );

    let terrainProvider: TerrainProvider;
    try {
      terrainProvider = shouldCorrectGeoid
        ? await this.withGeoidCorrection(providerLoad)
        : await providerLoad;
    } catch {
      // Fall back to an ellipsoid.
      terrainProvider = new EllipsoidTerrainProvider({
        ellipsoid: Ellipsoid.WGS84,
      });
    }

    if (this.isStaleTerrainRequest(request)) return;

    viewer.terrainProvider = terrainProvider;
    this._terrainProviderReadinessSignal.ready();
  }

  private async withGeoidCorrection(
    providerLoad: Promise<CesiumTerrainProvider>,
  ) {
    const [provider, grid] = await Promise.all([
      providerLoad,
      loadEgm96Grid().catch(() => null),
    ]);
    if (!grid) return provider;

    return new GeoidCorrectedTerrainProvider(provider, grid);
  }

  /**
   * Terrain providers load asynchronously, so an earlier request can settle
   * after a later one. Only the most recent request may set the terrain.
   */
  private isStaleTerrainRequest(request: number) {
    return request !== this._latestTerrainRequest;
  }

  private async setWorldImageryIon(ionAssetId: string) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    const ionId = this.parseIonAssetId(ionAssetId);
    if (ionAssetId != this._currentWorldImageryAssetId) {
      this._currentWorldImageryAssetId = ionAssetId;

      if (this._currentWorldImageryLayer != null) {
        viewer.imageryLayers.remove(this._currentWorldImageryLayer, true);
        this._currentWorldImageryLayer = null;
      }

      if (ionId != null) {
        const imageryLayer = ImageryLayer.fromProviderAsync(
          IonImageryProvider.fromAssetId(ionId),
        );
        viewer.imageryLayers.add(imageryLayer, 1);
        this._currentWorldImageryLayer = imageryLayer;
      }
    }
  }

  private async setWorldImageryMapTiler(mapTilerAssetId: string) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    const mapTilerKey = globalThis.__ENV?.RUNTIME_PUBLIC_MAPTILER_KEY;
    const defaultMapTilerWorldImagery = "satellite-v4";
    const assetId = encodeURIComponent(
      mapTilerAssetId || defaultMapTilerWorldImagery,
    );

    if (assetId != this._currentWorldImageryAssetId) {
      this._currentWorldImageryAssetId = assetId;

      if (this._currentWorldImageryLayer != null) {
        viewer.imageryLayers.remove(this._currentWorldImageryLayer, true);
        this._currentWorldImageryLayer = null;
      }

      if (assetId != null) {
        const imageryLayer = new ImageryLayer(
          // XXX These defaults are magic and need to be fixed for "real" custom
          // world imagery.
          new UrlTemplateImageryProvider({
            url: `https://api.maptiler.com/maps/${assetId}/{z}/{x}/{y}.jpg?key=${mapTilerKey}`,
            minimumLevel: 0,
            maximumLevel: 20,
            tileWidth: 512,
            tileHeight: 512,
          }),
        );
        viewer.imageryLayers.add(imageryLayer, 1);
        this._currentWorldImageryLayer = imageryLayer;
      }
    }
  }

  toggleTerrain(enabled: boolean) {
    if (!this._viewer.current) return;

    if (!enabled) {
      this._viewer.current.scene.globe.show = false;
      this.toggleSkyboxElements(false);
    } else {
      this._viewer.current.scene.globe.show = true;
      this.toggleSkyboxElements(true);
    }
  }

  async waitForTerrainLoaded(timeoutMs?: number) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return false;

    return new Promise<boolean>((resolve) => {
      if (viewer.scene.globe.tilesLoaded) {
        resolve(true);
        return;
      }

      const loadHandler = () => {
        if (viewer.scene.globe.tilesLoaded) {
          viewer.scene.postRender.removeEventListener(loadHandler);
          resolve(true);
        }
      };

      viewer.scene.postRender.addEventListener(loadHandler);

      if (timeoutMs) {
        setTimeout(() => {
          viewer.scene.postRender.removeEventListener(loadHandler);
          resolve(false);
        }, timeoutMs);
      }
    });
  }

  getViewerOpacity(): number {
    return this._viewer.current?.scene.globe.translucency.frontFaceAlpha ?? 1;
  }

  setOpacity(opacity: number) {
    if (!this._viewer.current) return;
    //avoiding alpha 1 to prevent lag when opacity is first set below 1
    const alpha = opacity >= 0 && opacity < 0.9999 ? opacity : 0.9999;
    this._viewer.current.scene.globe.translucency.frontFaceAlpha = alpha;
    this._viewer.current.scene.globe.translucency.enabled = true;
    this._viewer.current.scene.requestRender();
  }

  setUndergroundColor(newColor: string) {
    if (!this._viewer.current) return;

    if (!newColor) {
      this._viewer.current.scene.globe.undergroundColor = Color.BLACK;
      return;
    }

    this._viewer.current.scene.globe.undergroundColor =
      Color.fromCssColorString(newColor);
    this._backgroundColor = newColor;
    this._viewer.current.scene.requestRender();
  }

  setSceneColor(newColor: string) {
    if (!this._viewer.current) return;

    if (!newColor) {
      this._viewer.current.scene.backgroundColor = Color.fromCssColorString(
        this._backgroundColor,
      );
      return;
    }

    this._viewer.current.scene.backgroundColor =
      Color.fromCssColorString(newColor);
    this._backgroundColor = newColor;
    this._viewer.current.scene.requestRender();
  }

  /**
   * Subscribe to postRender for the underground monitor.
   * Returns an unsubscribe function.
   */
  onPostRender(handler: () => void): () => void {
    if (!this._viewer.current) return () => {};
    this._viewer.current.scene.postRender.addEventListener(handler);
    return () =>
      this._viewer.current?.scene.postRender.removeEventListener(handler);
  }

  private parseIonAssetId(ionId: string): number {
    if (ionId == null) return null;
    const id = +ionId;
    if (!id || Number.isNaN(id)) {
      console.error("Invalid terrain asset id", ionId);
      return null;
    }
    return id;
  }

  private toggleSkyboxElements(toggle: boolean) {
    if (!this._viewer.current) return;

    this._viewer.current.scene.skyAtmosphere.show = toggle;
    this._viewer.current.scene.skyBox.show = toggle;
    this._viewer.current.scene.sun.show = toggle;
    this._viewer.current.scene.moon.show = toggle;
    if (!toggle) {
      this._viewer.current.scene.backgroundColor = Color.fromCssColorString(
        this._backgroundColor,
      );
    }
    this._viewer.current.scene.requestRender();
  }
}
